import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Button, Meta } from '../../components/Atoms';
import AuthShell, { AuthLabel, AuthError, AuthSuccess, authInputStyle } from './AuthShell';

/**
 * Register w Terroir - zastępuje pages/Register.js.
 *
 * Backend wymaga silnego hasła (min 8 znaków, wielka, mała, cyfra, znak specjalny).
 * Po success - email verification flow (POST /api/auth/register zwraca info,
 * user dostaje email z linkiem aktywacyjnym).
 */

const PASSWORD_RULES = [
  { test: p => p.length >= 8, label: 'Min. 8 znaków' },
  { test: p => /[A-ZĄĆĘŁŃÓŚŹŻ]/.test(p), label: 'Wielka litera' },
  { test: p => /[a-ząćęłńóśźż]/.test(p), label: 'Mała litera' },
  { test: p => /\d/.test(p), label: 'Cyfra' },
  { test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p), label: 'Znak specjalny' },
];

function PasswordChecklist({ password }) {
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {PASSWORD_RULES.map((rule, i) => {
        const ok = rule.test(password);
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11,
            color: ok ? 'var(--t-sage)' : 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)',
            letterSpacing: '.06em',
            transition: 'color 200ms ease',
          }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
              border: `1.5px solid ${ok ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
              background: ok ? 'var(--t-sage)' : 'transparent',
              flexShrink: 0, position: 'relative',
            }}>
              {ok && (
                <svg width="8" height="8" viewBox="0 0 14 14" fill="none"
                     style={{ position: 'absolute', top: 1, left: 1 }}>
                  <path d="M3 7.5l3 3 6-6.5" stroke="var(--t-bg)" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {rule.label}
          </div>
        );
      })}
    </div>
  );
}

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const allRulesPassed = PASSWORD_RULES.every(r => r.test(password));
  const canSubmit = username.length >= 3 && email && allRulesPassed && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    const result = await register(username, email, password);
    if (result.success) {
      setSuccess(result.message || 'Konto utworzone! Sprawdź email, żeby je aktywować.');
      setLoading(false);
    } else {
      setError(result.error || 'Błąd rejestracji');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell
        eyebrow="Już prawie"
        title={<>Sprawdź <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>skrzynkę</span>.</>}
        subtitle="Wysłaliśmy link aktywacyjny. Kliknij w niego, żeby potwierdzić adres i zacząć korzystać z aplikacji."
        footer={
          <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>
            Nie dotarł?{' '}
            <Link to="/login" style={{
              color: 'var(--t-sage)', textDecoration: 'none', fontWeight: 500,
            }}>Wróć do logowania</Link> i zaloguj się — wyślemy ponownie.
          </div>
        }
      >
        <AuthSuccess>{success}</AuthSuccess>
        <Meta style={{ textAlign: 'center', marginTop: 20 }}>
          Wiadomość może być w spamie · sprawdź też tam
        </Meta>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Załóż konto"
      title={<>Twój ogród <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>czeka</span>.</>}
      subtitle="GardenApp.pl pamięta co i kiedy siejesz. W następnym sezonie sam Ci przypomni."
      footer={
        <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>
          Masz już konto?{' '}
          <Link to="/login" style={{
            color: 'var(--t-sage)', textDecoration: 'none', fontWeight: 500,
          }}>Zaloguj się</Link>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AuthError>{error}</AuthError>

        <div style={{ marginBottom: 16 }}>
          <AuthLabel htmlFor="username">Nazwa użytkownika</AuthLabel>
          <input
            id="username"
            type="text"
            autoComplete="username"
            required
            minLength={3}
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="np. kasia_garden"
            style={authInputStyle}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="twoj@email.com"
            style={authInputStyle}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <AuthLabel htmlFor="password">Hasło</AuthLabel>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            style={authInputStyle}
          />
          {password && <PasswordChecklist password={password} />}
        </div>

        <Button
          type="submit"
          full
          variant={canSubmit ? 'primary' : 'ghost'}
        >{loading ? 'Tworzymy konto…' : 'Załóż konto'}</Button>

        <div style={{
          marginTop: 16, textAlign: 'center',
          fontSize: 11, color: 'var(--t-text-mute)', lineHeight: 1.5,
        }}>
          Klikając „Załóż konto" akceptujesz{' '}
          <Link to="/privacy-policy" style={{ color: 'var(--t-text-dim)' }}>
            politykę prywatności
          </Link>.
        </div>
      </form>
    </AuthShell>
  );
}
